import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, Account } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T> | T[] | null | undefined,
): PaginatedResponse<T> {
    if (!raw) {
        return { items: [], total: 0, page: 1, limit: 10, totalPages: 0 }
    }

    if (Array.isArray(raw)) {
        return {
            items: raw,
            total: raw.length,
            page: 1,
            limit: Math.max(10, raw.length),
            totalPages: 1,
        }
    }

    if ('items' in raw) {
        return {
            ...raw,
            items: raw.items,
        }
    }

    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

interface UserApiResponse {
    id?: unknown
    userId?: unknown
    username?: unknown
    email?: unknown
    fullName?: unknown
    phoneNumber?: unknown
    phone?: unknown
    description?: unknown
    note?: unknown
    status?: number
    createdAt?: unknown
    updatedAt?: unknown
    unitId?: unknown
    unitName?: unknown
    groupPermissionId?: unknown
    groupPermissionName?: unknown
    roleName?: unknown
    loginCount?: number
}

function asString(value: unknown): string | undefined {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return undefined
}

function normalizeAccount(raw: UserApiResponse): Account {
    const id = asString(raw.id) ?? asString(raw.userId) ?? ''
    return {
        id,
        username: asString(raw.username) ?? '',
        fullName: asString(raw.fullName) ?? '',
        email: asString(raw.email) ?? '',
        phone: asString(raw.phoneNumber) ?? asString(raw.phone) ?? '',
        note: asString(raw.description) ?? asString(raw.note),
        status: raw.status ?? 1,
        createdAt: asString(raw.createdAt) ?? '',
        updatedAt: asString(raw.updatedAt) ?? asString(raw.createdAt) ?? '',
        unitId: asString(raw.unitId),
        unitName: asString(raw.unitName),
        groupPermissionId: asString(raw.groupPermissionId),
        groupPermissionName: asString(raw.groupPermissionName),
        roleName: asString(raw.roleName),
        loginCount: raw.loginCount ?? 1,
    }
}

function adaptAccountPage(
    raw: SpringPage<UserApiResponse> | PaginatedResponse<UserApiResponse> | UserApiResponse[] | null | undefined,
): PaginatedResponse<Account> {
    const page = adaptPage(raw)

    return {
        ...page,
        items: page.items.map((item) => normalizeAccount(item)),
    }
}

interface CreateUserPayload {
    username: string
    email: string
    fullName: string
    phoneNumber: string
    description: string
    unitId?: string
    groupPermissionId?: string
}

interface UpdateUserPayload extends CreateUserPayload {
    status: number
}

function toCreatePayload(
    data: Omit<Account, 'id' | 'createdAt' | 'updatedAt' | 'status'> &
        Partial<Pick<Account, 'status'>>,
): CreateUserPayload {
    return {
        username: data.username,
        email: data.email,
        fullName: data.fullName,
        phoneNumber: data.phone,
        description: data.note ?? '',
        unitId: data.unitId,
        groupPermissionId: data.groupPermissionId,
    }
}

function toUpdatePayload(
    data: Partial<Omit<Account, 'id' | 'createdAt' | 'updatedAt'>>,
): Partial<UpdateUserPayload> {
    const payload: Partial<UpdateUserPayload> = {}
    if (data.username !== undefined) payload.username = data.username
    if (data.email !== undefined) payload.email = data.email
    if (data.fullName !== undefined) payload.fullName = data.fullName
    if (data.phone !== undefined) payload.phoneNumber = data.phone
    if (data.note !== undefined) payload.description = data.note
    if (data.status !== undefined) payload.status = data.status
    if (data.unitId !== undefined) payload.unitId = data.unitId
    if (data.groupPermissionId !== undefined) payload.groupPermissionId = data.groupPermissionId
    return payload
}


const USER_BASE = '/v1/user-management'

export interface AccountQueryParams {
    username?: string
    email?: string
    fullName?: string
    status?: number
    fromDate?: string
    toDate?: string
    unitId?: string
}

/**
 * Fetch accounts list with pagination and filters.
 */
export async function fetchAccounts(
    page: number,
    pageSize: number,
    params: AccountQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<Account>> {
    const { username, email, fullName, status, fromDate, toDate, unitId } = params

    try {
        const response = await api.get<ApiEnvelope<SpringPage<UserApiResponse> | UserApiResponse[]>>(
            `${USER_BASE}/list-user`,
            {
                params: {
                    page: page,
                    size: pageSize,
                    username: username || undefined,
                    email: email || undefined,
                    fullName: fullName || undefined,
                    status: status === undefined ? undefined : status,
                    fromDate,
                    toDate,
                    unitId: unitId || undefined,
                },
                signal
            },
        )

        return adaptAccountPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách tài khoản.';
        throw new Error(message);
    }
}

/**
 * Get a single account by id using the dedicated detail endpoint.
 */
export async function getAccountById(
    id: string,
    signal?: AbortSignal
): Promise<Account> {
    try {
        const response = await api.get<ApiEnvelope<UserApiResponse>>(
            `${USER_BASE}/detail-user/${id}`,
            { signal }
        )

        return normalizeAccount(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thông tin tài khoản.';
        throw new Error(message);
    }
}

/**
 * Create a new account.
 */
export async function createAccount(
    data: Omit<Account, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<ApiEnvelope<Account>> {
    let response;

    try {
        response = await api.post<ApiEnvelope<UserApiResponse>>(
            `${USER_BASE}/create-user`,
            toCreatePayload(data),
        )
    } catch (error: any) {
        if (error.response) {
            console.error('Backend Response Data:', error.response.data)
        }
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo tài khoản.';
        throw new Error(message);
    }

    const responseData = response.data.responseData

    let accountData: Account;
    if (typeof responseData !== 'object' || Array.isArray(responseData)) {
        console.warn('Backend returned unexpected responseData format. Bypassing normalization.', responseData)
        accountData = { id: '', ...data, status: data.status } as Account
    } else {
        try {
            accountData = normalizeAccount(responseData)
        } catch (error) {
            accountData = { id: '', ...data, status: data.status } as Account
        }
    }

    return {
        ...response.data,
        responseData: accountData,
    }
}

/**
 * Update an existing account.
 */
export async function updateAccount(
    id: string,
    data: Partial<Omit<Account, 'id' | 'createdAt' | 'updatedAt'>>,
): Promise<ApiEnvelope<Account>> {
    try {
        const response = await api.put<ApiEnvelope<UserApiResponse>>(
            `${USER_BASE}/update-user/${id}`,
            toUpdatePayload(data),
        )

        let accountData: Account;
        if (typeof response.data.responseData !== 'object') {
            accountData = { id, ...data } as Account
        } else {
            accountData = normalizeAccount(response.data.responseData)
        }

        return {
            ...response.data,
            responseData: accountData,
        }
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật tài khoản.';
        throw new Error(message);
    }
}

/**
 * Delete an account.
 */
export async function deleteAccount(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`${USER_BASE}/delete-user/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa tài khoản.';
        throw new Error(message);
    }
}

/**
 * Resend the activation email
 */
export async function sendActivationMail(userId: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.post<ApiEnvelope<any>>(
            `${USER_BASE}/send-activate-mail`,
            { userId },
        )
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi gửi lại mail kích hoạt.';
        throw new Error(message);
    }
}

/**
 * Send the default-password reset email
 */
export async function sendResetPassword(userId: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.post<ApiEnvelope<any>>(
            `${USER_BASE}/send-reset-password`,
            { userId },
        )
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi đặt lại mật khẩu mặc định.';
        throw new Error(message);
    }
}

/**
 * Toggle the active status of an account. 
 */
export async function updateAccountStatus(
    account: Account,
    newIsActive: number,
): Promise<ApiEnvelope<Account>> {
    return updateAccount(account.id, {
        ...account,
        status: newIsActive
    })
}